import { Suspense, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { EffectComposer, Bloom, Vignette } from '@react-three/postprocessing';
import { KernelSize } from 'postprocessing';
import { Link } from 'react-router-dom';
import * as THREE from 'three';
import { ArrowRight, BadgeCheck, Sparkles, Globe2 } from 'lucide-react';
import Earth from './Earth';
import Atmosphere from './Atmosphere';
import StarField from './StarField';
import LandPin from './LandPin';
import useDeviceQuality from './useDeviceQuality';

/* Pin dei terreni italiani — lat/lon reali, 5 regioni chiave */
const ITALIAN_LANDS = [
  { lat: 45.46, lon: 9.19,   label: 'Milano' },
  { lat: 43.77, lon: 11.25,  label: 'Toscana', pinned: true },
  { lat: 41.90, lon: 12.49,  label: 'Roma' },
  { lat: 40.80, lon: 17.20,  label: 'Puglia' },
  { lat: 37.50, lon: 14.00,  label: 'Sicilia' },
];

function LoadingScreen() {
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="w-14 h-14 rounded-full border-2 border-white/10"/>
          <div className="absolute inset-0 w-14 h-14 rounded-full border-2 border-transparent animate-spin"
               style={{ borderTopColor: 'rgb(180,255,0)' }}/>
        </div>
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60">
          Entering AgriNexus
        </div>
      </div>
    </div>
  );
}

function SceneContent({ quality, globePos, globeScale, labelPortal }) {
  const isMobile = quality.isMobile;
  const earthRef = useRef();
  const cloudsRef = useRef();
  const camRef = useRef();
  const startTime = useRef(performance.now());
  const target = [0, 0.25, 3.8];
  const from = [0, 0.5, 9];

  useFrame(() => {
    if (!camRef.current) return;
    const t = Math.min(1, (performance.now() - startTime.current) / 2000);
    const e = 1 - Math.pow(1 - t, 3); // easeOutCubic
    camRef.current.position.set(
      from[0] + (target[0] - from[0]) * e,
      from[1] + (target[1] - from[1]) * e,
      from[2] + (target[2] - from[2]) * e,
    );
    camRef.current.updateProjectionMatrix();
  });

  return (
    <>
      <PerspectiveCamera ref={camRef} makeDefault fov={38} position={from}/>

      {/* Lights */}
      <ambientLight intensity={0.09}/>
      <directionalLight position={[4, 2.5, 2]} intensity={2.0} color="#fff2d4"/>
      <directionalLight position={[-3, -1, -2]} intensity={0.35} color="#b4ff00"/>

      <StarField count={quality.starsCount} radius={30}/>

      <group position={globePos} scale={globeScale}>
        <Earth radius={1} autoRotate earthRef={earthRef} cloudsRef={cloudsRef} lite={quality.lite} segments={quality.segments}/>
        <Atmosphere radius={1} color="#8dd8ff" intensity={1.1}/>
        <Atmosphere radius={1} color="#b4ff00" intensity={0.55}/>

        {/* Pin agganciati alla rotazione dell'Earth group (stessa orientazione) */}
        <group rotation={[0, THREE.MathUtils.degToRad(-102.5), THREE.MathUtils.degToRad(-12)]}>
          {ITALIAN_LANDS.map((p, i) => (
            <LandPin key={i} {...p} radius={1} labelPortal={labelPortal}/>
          ))}
        </group>
      </group>

      <OrbitControls
        enabled={!isMobile}
        enablePan={false}
        enableZoom={false}
        enableDamping
        dampingFactor={0.08}
        rotateSpeed={0.35}
        target={globePos}
        minPolarAngle={Math.PI * 0.35}
        maxPolarAngle={Math.PI * 0.68}
      />

      {quality.bloom && (
        <EffectComposer disableNormalPass multisampling={0}>
          <Bloom
            intensity={0.72}
            luminanceThreshold={0.4}
            luminanceSmoothing={0.3}
            kernelSize={KernelSize.LARGE}
            mipmapBlur
          />
          <Vignette eskil={false} offset={0.22} darkness={0.8}/>
        </EffectComposer>
      )}
    </>
  );
}

export default function Hero3D() {
  const quality = useDeviceQuality();
  const [ready, setReady] = useState(false);
  const labelPortal = useRef(null);

  // Globo spostato a destra per lasciare spazio al testo
  const globePos = quality.isMobile ? [0, 0.45, 0] : [0.9, -0.05, 0];
  const globeScale = quality.isMobile ? 0.48 : 1;

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at 70% 55%, #0d1826 0%, #050709 55%, #000 100%)' }}
    >
      {/* Vignette CSS */}
      <div aria-hidden className="absolute inset-0 pointer-events-none z-10"
           style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.55) 100%)' }}/>
      {/* chartreuse ambient bottom glow */}
      <div aria-hidden
           className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%] h-[260px] blur-[120px] opacity-25 z-0 pointer-events-none"
           style={{ background: 'radial-gradient(ellipse, rgba(180,255,0,0.6) 0%, transparent 70%)' }}/>

      <div className="relative min-h-[100vh] flex flex-col justify-center">
        {/* === TEXT OVERLAY === */}
        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 pt-28 md:pt-24 pb-10 w-full pointer-events-none">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.28em] font-bold mb-7 pointer-events-auto"
                 style={{
                   background: 'rgba(255,255,255,0.06)',
                   border: '1px solid rgba(255,255,255,0.1)',
                   backdropFilter: 'blur(14px)',
                   color: '#e9f3dd',
                 }}>
              <Globe2 size={12} style={{ color: 'rgb(180,255,0)' }}/>
              Ecosistema AgriTech · Italia
            </div>

            <h1 className="font-display tracking-tighter leading-[0.95] text-white"
                style={{ fontSize: 'clamp(2.4rem, 5.1vw, 4.8rem)', textShadow: '0 2px 60px rgba(0,0,0,0.75)' }}>
              La terra italiana,<br/>
              <span className="italic font-light whitespace-nowrap" style={{
                background: 'linear-gradient(100deg, #d8ff6e 0%, #b4ff00 45%, #76ab00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>vista dallo spazio.</span>
            </h1>

            <p className="mt-6 text-base md:text-lg leading-relaxed max-w-md pointer-events-auto"
               style={{ color: 'rgba(240,245,230,0.78)', textShadow: '0 1px 20px rgba(0,0,0,0.6)' }}>
              Scopri, affitta e stima terreni agricoli italiani. Mappa satellitare,
              AI predittiva, zero intermediari.
            </p>

            <div className="mt-9 flex flex-wrap gap-3 pointer-events-auto">
              <Link to="/esplora"
                data-testid="home-hero-cta"
                className="group inline-flex items-center gap-2 h-12 px-7 rounded-full text-sm font-semibold transition-all hover:scale-[1.03]"
                style={{
                  background: 'rgb(180,255,0)',
                  color: '#0A0D10',
                  boxShadow: '0 0 0 1px rgba(180,255,0,0.4), 0 10px 40px rgba(180,255,0,0.35), inset 0 1px 0 rgba(255,255,255,0.4)',
                }}>
                Esplora i terreni
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform"/>
              </Link>
              <Link to="/pubblica"
                data-testid="home-publish-cta"
                className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-sm font-semibold transition-all hover:bg-white/10"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(10px)',
                  color: '#f0f5e6',
                }}>
                Pubblica il tuo
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] pointer-events-auto"
                 style={{ color: 'rgba(240,245,230,0.6)' }}>
              <span className="inline-flex items-center gap-1.5">
                <BadgeCheck size={12} style={{ color: 'rgb(180,255,0)' }}/> Proprietari verificati
              </span>
              <span className="w-1 h-1 rounded-full" style={{ background: 'rgba(240,245,230,0.3)' }}/>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles size={12} style={{ color: 'rgb(180,255,0)' }}/> Yield AI PRO gratis
              </span>
              <span className="w-1 h-1 rounded-full" style={{ background: 'rgba(240,245,230,0.3)' }}/>
              <span>Dati NASA · ISMEA · PNRR</span>
            </div>
          </div>
        </div>

        {/* === 3D CANVAS === */}
        <div className={`absolute inset-0 z-0 ${quality.isMobile ? 'pointer-events-none' : 'hero3d-grab'}`}
             style={quality.isMobile ? { touchAction: 'pan-y' } : undefined}>
          {!ready && <LoadingScreen/>}
          <Canvas
            dpr={quality.dpr}
            resize={{ offsetSize: true }}
            gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
            onCreated={() => setTimeout(() => setReady(true), 200)}
            data-testid="hero-3d-canvas"
          >
            <Suspense fallback={null}>
              <SceneContent quality={quality} globePos={globePos} globeScale={globeScale} labelPortal={labelPortal}/>
            </Suspense>
          </Canvas>
          <div ref={labelPortal} className="absolute inset-0 pointer-events-none overflow-hidden"/>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-7 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5 pointer-events-none">
          <div className="text-[9px] uppercase tracking-[0.3em] font-bold" style={{ color: 'rgba(240,245,230,0.5)' }}>
            Scroll
          </div>
          <div className="w-[1px] h-9 relative overflow-hidden" style={{ background: 'rgba(240,245,230,0.15)' }}>
            <div className="absolute left-0 top-0 w-[1px] h-3 animate-scroll-ind" style={{ background: 'rgb(180,255,0)' }}/>
          </div>
        </div>
      </div>
    </section>
  );
}
